import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    KPI_DATA_CHANGED_EVENT,
    KPI_DATA_CHANGED_STORAGE_KEY,
    KpiDataChangedDetail,
} from 'services/kpiDataEvents';
import { KpiService } from './kpiService';
import { ClientKpiConfiguration, DashboardKpi, KpiDashboardResponse, KpiDefinition, KpiFilters } from './types';

export const useKpiDashboard = (filters: KpiFilters) => {
    const [definitions, setDefinitions] = useState<KpiDefinition[]>([]);
    const [configuration, setConfiguration] = useState<ClientKpiConfiguration[]>([]);
    const [response, setResponse] = useState<KpiDashboardResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        setConfiguration([]);
        setResponse(null);
        setLoading(true);
        setError(false);
        Promise.all([
            KpiService.getAvailableKpis(),
            KpiService.getClientKpiConfiguration(filters.dashboardTabId, filters.tenantName, filters.userId),
        ]).then(([nextDefinitions, nextConfiguration]) => {
            if (!active) return;
            setDefinitions(nextDefinitions);
            setConfiguration(nextConfiguration);
        }).catch(() => {
            if (!active) return;
            setError(true);
            setLoading(false);
        });
        return () => { active = false; };
    }, [filters.dashboardTabId, filters.tenantName, filters.userId]);

    const hasEnabledKpis = useMemo(
        () => configuration.some((item) => item.dashboardTabId === filters.dashboardTabId && item.enabled),
        [configuration, filters.dashboardTabId]
    );

    useEffect(() => {
        let active = true;

        if (!hasEnabledKpis) {
            setResponse(null);
            setError(false);
            setLoading(false);
            return () => { active = false; };
        }

        setLoading(true);
        setError(false);
        KpiService.getKpiResults(filters).then((nextResults) => {
            if (active) setResponse(nextResults);
        }).catch(() => {
            if (active) setError(true);
        }).finally(() => {
            if (active) setLoading(false);
        });

        return () => { active = false; };
    }, [filters.dashboardTabId, filters.programId, filters.schoolYearId, filters.termTypeId, hasEnabledKpis]);

    const refreshResults = useCallback(() => {
        if (!hasEnabledKpis) return;

        KpiService.getKpiResults(filters).then((nextResults) => {
            setResponse(nextResults);
            setError(false);
        }).catch((refreshError) => console.error('Failed to refresh KPI results:', refreshError));
    }, [filters.dashboardTabId, filters.programId, filters.schoolYearId, filters.termTypeId, hasEnabledKpis]);

    const isCurrentUserEvent = useCallback((detail: KpiDataChangedDetail): boolean =>
        detail.tenantName === filters.tenantName && detail.userId === filters.userId,
    [filters.tenantName, filters.userId]);

    const handleKpiDataChanged = useCallback((event: Event) => {
        const detail = (event as CustomEvent<KpiDataChangedDetail>).detail;
        if (detail && isCurrentUserEvent(detail)) refreshResults();
    }, [isCurrentUserEvent, refreshResults]);

    const handleKpiDataStorageChanged = useCallback((event: StorageEvent) => {
        if (event.key !== KPI_DATA_CHANGED_STORAGE_KEY || !event.newValue) return;

        try {
            const detail = JSON.parse(event.newValue) as KpiDataChangedDetail;
            if (isCurrentUserEvent(detail)) refreshResults();
        } catch (error) {
            console.error('Failed to process KPI data change:', error);
        }
    }, [isCurrentUserEvent, refreshResults]);

    useEffect(() => {
        window.addEventListener(KPI_DATA_CHANGED_EVENT, handleKpiDataChanged);
        window.addEventListener('storage', handleKpiDataStorageChanged);

        return () => {
            window.removeEventListener(KPI_DATA_CHANGED_EVENT, handleKpiDataChanged);
            window.removeEventListener('storage', handleKpiDataStorageChanged);
        };
    }, [handleKpiDataChanged, handleKpiDataStorageChanged]);

    const kpis = useMemo<DashboardKpi[]>(() => definitions.flatMap((definition) => {
        return configuration.filter((item) =>
            item.kpiId === definition.id &&
            item.enabled &&
            (definition.supportsParent !== false || item.viewType !== 'parent')
        ).flatMap((config) => {
            const result = response?.results.find((item) => item.kpiId === definition.id && item.viewType === config.viewType);
            return result ? [{ ...definition, ...config, ...result }] : [];
        });
    }).sort((a, b) => {
        return a.displayOrder - b.displayOrder;
    }), [configuration, definitions, response]);

    const reorderKpis = useCallback((activeId: string, overId: string) => {
        setConfiguration((current) => {
            const enabledForTab = current
                .filter((item) => item.dashboardTabId === filters.dashboardTabId && item.enabled)
                .sort((a, b) => a.displayOrder - b.displayOrder);
            const getItemId = (item: ClientKpiConfiguration) => `${item.kpiId}:${item.viewType}`;
            const activeIndex = enabledForTab.findIndex((item) => getItemId(item) === activeId);
            const overIndex = enabledForTab.findIndex((item) => getItemId(item) === overId);

            if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) return current;

            const reordered = [...enabledForTab];
            const [movedItem] = reordered.splice(activeIndex, 1);
            reordered.splice(overIndex, 0, movedItem);
            const disabledForTab = current
                .filter((item) => item.dashboardTabId === filters.dashboardTabId && !item.enabled)
                .sort((a, b) => a.displayOrder - b.displayOrder);
            const orderById = new Map([...reordered, ...disabledForTab]
                .map((item, index) => [getItemId(item), index + 1]));
            const updated = current.map((item) => {
                const displayOrder = orderById.get(getItemId(item));
                return displayOrder === undefined ? item : { ...item, displayOrder };
            });

            KpiService.saveClientKpiConfiguration(updated, filters.tenantName, filters.userId)
                .catch((saveError) => console.error('Failed to save KPI order:', saveError));
            return updated;
        });
    }, [filters.dashboardTabId, filters.tenantName, filters.userId]);

    const toggleKpi = useCallback((kpiId: string, viewType: ClientKpiConfiguration['viewType']) => {
        setConfiguration((current) => {
            const updated = current.map((item) => item.kpiId === kpiId && item.viewType === viewType ? { ...item, enabled: !item.enabled } : item);
            KpiService.saveClientKpiConfiguration(updated, filters.tenantName, filters.userId).catch((saveError) => console.error('Failed to save KPI configuration:', saveError));
            return updated;
        });
    }, [filters.tenantName, filters.userId]);

    const disableAllKpis = useCallback(() => {
        setConfiguration((current) => {
            const updated = current.map((item) => ({ ...item, enabled: false }));
            KpiService.saveClientKpiConfiguration(updated, filters.tenantName, filters.userId).catch((saveError) => console.error('Failed to save KPI configuration:', saveError));
            return updated;
        });
    }, [filters.tenantName, filters.userId]);

    return { definitions, configuration, kpis, serviceDates: response, hasEnabledKpis, loading, error, toggleKpi, disableAllKpis, reorderKpis };
};
